import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { DatePickerInput } from '@mantine/dates';
import { Calendar } from 'tabler-icons-react';
import { Divider } from '@mantine/core';
import AppStack from '../../common/AppStack';
import ResponsiveModal from '../../common/ResponsiveModal';
import { Context as AuthContext } from '../../../providers/AuthProvider';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import AppText from '../../common/AppText';
import { triggerNotification } from '../../../helpers/notificationHelper';
import CustomNumberInput from '../../common/CustomNumberInput';
import { formatLocalDate } from '../../../helpers/format';

const OrderPaymentInstallmentEditModal = ({
  isOpen,
  onClose,
  pkRegCart,
  installment
}) => {
  const { state: authState } = useContext(AuthContext);
  const { updateRegCartPaymentInstallment } = useContext(
    RegistrationAdminContext
  );
  const [formState, setFormState] = useState({
    amount: null,
    fixedDueDate: null,
    loading: false
  });

  useEffect(() => {
    if (isOpen) {
      setFormState({
        amount: installment.chargeAmount,
        fixedDueDate: new Date(formatLocalDate(installment.dueDate)),
        loading: false
      });
    }
  }, [isOpen]);

  return (
    <ResponsiveModal
      formSectionProps={{
        cancelTitle: 'Close',
        onCancel: onClose,
        submitTitle: `Update Installment`,
        submitLoading: formState.loading,
        onSubmit: () => {
          setFormState({ ...formState, loading: true });
          updateRegCartPaymentInstallment(
            pkRegCart,
            {
              pkRegCartPaymentInstallment: installment.entityId,
              totalInCents: formState.amount,
              chargeDate: formState.fixedDueDate
            },
            () => {
              triggerNotification('Payment processed', 'Success', 'green');
              onClose();
            },
            (e) => {
              triggerNotification(e);
              setFormState({ ...formState, loading: false });
            }
          );
        }
      }}
      isNewModalStyle
      isOpen={isOpen}
      onClose={onClose}
      size={500}
      title="Update installment?"
    >
      {!installment ? (
        <AppStack>
          <AppText
            style={{
              fontSize: 14,
              textAlign: 'center',
              color: '#666',
              fontStyle: 'italic'
            }}
          >
            No installment selected.
          </AppText>
        </AppStack>
      ) : (
        <AppStack style={{ gap: 16 }}>
          {authState.userData.isAdmin && (
            <CustomNumberInput
              allowNegative={false}
              decimalScale={2}
              disabled={formState.loading}
              fixedDecimalScale
              label="Charge Amount"
              leftSection={
                <AppText
                  style={{
                    fontWeight: 500,
                    color: '#666',
                    fontSize: 14
                  }}
                >
                  $
                </AppText>
              }
              min={0}
              onChange={(v) => {
                setFormState({
                  ...formState,
                  amount: v * 100
                });
              }}
              placeholder="25"
              required
              style={{ flex: 1 }}
              value={formState.amount / 100}
            />
          )}

          <DatePickerInput
            clearable
            disabled={formState.loading}
            label="Charge Date"
            leftSection={<Calendar color="#000" size={16} />}
            minDate={new Date()}
            onChange={(value) =>
              setFormState({
                ...formState,
                errors: {},
                fixedDueDate: value
              })
            }
            placeholder="Select date"
            required
            style={{ flex: 1 }}
            value={formState.fixedDueDate}
            valueFormat="MMM D, YYYY"
          />

          <Divider />

          <AppText
            style={{
              fontSize: 14,
              color: '#666',
              textAlign: 'center',
              fontStyle: 'italic'
            }}
          >
            All other installments in the payment plan will remain unchanged.
          </AppText>
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

OrderPaymentInstallmentEditModal.propTypes = {
  installment: PropTypes.object,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  pkRegCart: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

export default OrderPaymentInstallmentEditModal;
